import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { useLazyListFreeloadingQuery } from "store/services/freeloadingApi";

const FreeloadingListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const { isDriver, loggedInUser, viewEntity } = useAuth();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    { title: "Loading", path: "/app/crm/shipment-loading/list" },
    { title: "Free Loading" },
  ];

  const freeloadingTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Loading ID",
      selector: (row) => row?.loadingId,
      cell: (row) => {
        const displayId = row?.loadingId;
        const path = !isDriver
          ? `/app/crm/shipment-loading/${row?._id}/detail`
          : `/app/driver/shipment-loading/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Shipment ID",
      selector: (row) => row?.shipments,
      cell: (row) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = React.useState(null);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedShipments, setSelectedShipments] = React.useState([]);

        const shipments = Array.isArray(row?.shipments) ? row.shipments : [];
        const visibleShipments = shipments.slice(0, 2);
        const remainingCount = shipments.length - visibleShipments.length;

        const handleChipClick = (event, allShipments) => {
          setAnchorEl(event.currentTarget);
          setSelectedShipments(allShipments);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return shipments.length > 0 ? (
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {visibleShipments.map((shipment, index) => (
                <span key={index} style={{ fontSize: "0.9rem" }}>
                  {shipment.IsReturning
                    ? `${shipment.shipmentId} (R)`
                    : shipment.shipmentId}
                  {index !== visibleShipments.length - 1 && ","}
                </span>
              ))}
              {remainingCount > 0 && (
                <Tooltip title="Click to view all Shipment IDs">
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    sx={{
                      marginLeft: "4px",
                      height: "18px",
                      fontSize: "10px",
                      backgroundColor: "#e0e0e0",
                      color: "#333",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleChipClick(e, shipments)}
                  />
                </Tooltip>
              )}
            </Box>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box p={2} minWidth={100}>
                {selectedShipments.map((shipment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {shipment.IsReturning
                      ? `${shipment.shipmentId} (R)`
                      : shipment.shipmentId}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Collection ID",
      selector: (row) => row?.collection?.collectionId,
      cell: (row) => row?.collection?.collectionId || "N/A",
    },
    {
      name: "Consolidation ID",
      selector: (row) => row?.consolidation?.consolidationId,
      cell: (row) => row?.consolidation?.consolidationId || "N/A",
    },
    {
      name: "Loading Time",
      selector: (row) => <AppTableDateTime date={row?.loadingTime} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              !isDriver
                ? `/app/crm/shipment-loading/${row?._id}/detail`
                : `/app/driver/shipment-loading/${row?._id}/detail`
            }
          >
            <IconButton
              size="small"
              aria-label="View Freeloading"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          {!isDriver && (
            // <Link to={`/app/crm/freeloading/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Freeloading" disabled>
              <ModeEditOutlineTwoTone />
            </IconButton>
            // </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchFreeloading,
    { data: freeloadingRes, isLoading, isFetching, error },
  ] = useLazyListFreeloadingQuery();

  React.useEffect(() => {
    fetchFreeloading({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      driverId: loggedInUser.typeRef,
      ...(keyword && { keyword }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [
    activeTab,
    currentPage,
    fetchFreeloading,
    loggedInUser,
    keyword,
    perPage,
    viewEntity,
  ]);

  useAppMeta(`${activeTab} Freeloading`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={freeloadingTabs} defaultValue={activeTab} />
        <TableFilter
          searchLabel="Loading ID"
          disabled={isLoading || isFetching}
        />
        <AppTable
          columns={columns}
          data={freeloadingRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={freeloadingRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default FreeloadingListScreen;

const PageActions = () => {
  const { isDriver } = useAuth();
  return (
    <>
      {!isDriver && (
        <Link to="/app/crm/freeloading/create">
          <Button startIcon={<Add />} variant="contained" color="primary">
            Add Freeloading
          </Button>
        </Link>
      )}
    </>
  );
};
